import { Component } from 'react'
import { connect } from 'react-redux'
import { Container, Loader } from '../../cdl-react-components/components'
import { sortPropFn, sortPropDescFn, graphqlFetch } from '../../cdl-react-components/utilities'
import { logEvent } from '../../../utilities'
import { setConfiguration } from '../../../store/actions'
import { CONTENT_MODE } from '../../../utilities/constants'
import LearningLibraryDesktop from './LearningLibraryDesktop'
import LearningLibraryMobile from './LearningLibraryMobile'
import { distinctByProp } from '../../../utilities'

class LearningLibrary extends Component {
    state = {
        loading: true,
        groups: undefined,
        periods: undefined,
        articles: undefined,
        filteredArticles: undefined,
        recentArticles: undefined,
        allGroupsSelected: true,
        allPeriodsSelected: true,
    }

    init = (cache) => {
        this.setState({ 
            loading: false,
            groups: cache.groups,
            periods: cache.periods,
            articles: cache.articles,
            filteredArticles: cache.articles,
            recentArticles: cache.recentArticles,
            allGroupsSelected: true,
            allPeriodsSelected: true
        })
    }

    updateAllSelected = () => {
        this.setState({ 
            allGroupsSelected: this.state.groups.reduce((acc, x) => acc && x.active, true),
            allPeriodsSelected: this.state.periods.reduce((acc, x) => acc && x.active, true)
        },
        this.filterArticles)
    }

    toggleCheckbox = (prop) => (id) => {
        const items = 
            this.state[prop].map(x =>
                x.id === id ?
                    { ...x, active: !x.active } : x)

        this.setState({ [prop]: items }, this.updateAllSelected)
    }
    togglePeriod = this.toggleCheckbox('periods')
    toggleGroup = this.toggleCheckbox('groups')

    toggleAll = (prop, allProp) => () => {
        this.setState({ 
            [prop]: this.state[prop].map(x => ({ ...x, active: !this.state[allProp] }) ),
            [allProp]: !this.state[allProp]
        }, this.filterArticles)
    }
    toggleAllPeriods = this.toggleAll('periods', 'allPeriodsSelected')
    toggleAllGroups = this.toggleAll('groups', 'allGroupsSelected')

    filterArticles = () => {
        const { groups, periods, articles } = this.state

        let filteredArticles = articles.reduce((acc, articleGroup) => {
            return groups.find(x => x.name === articleGroup.groupName).active ?
                [...acc, articleGroup] :
                acc
        }, [])

        // only filter by period if atleast one is inactive
        if (periods.map(x => x.active).indexOf(false) > -1) {
            const activePeriodIds = periods.filter(x => x.active).map(x => x.id)

            filteredArticles = filteredArticles.map(articleGroup => {
                const filtered = articleGroup.articles
                    .filter(article => {
                        return article.ages.length > 0 &&
                            article.ages
                                .map(y => activePeriodIds.indexOf(y.id) >= 0)
                                .reduce((acc, y) => acc || y, false)
                    })
                return { ...articleGroup, articles: filtered }
            })

            filteredArticles = filteredArticles.filter(x => x.articles.length > 0)
        }


        this.setState({ filteredArticles })
    }

    render() {
        const { contentMode } = this.props
        const { loading, groups, periods, filteredArticles, recentArticles, allGroupsSelected, allPeriodsSelected } = this.state

        return (
            <Container className="learning-library">
                {!loading ? (
                    contentMode === CONTENT_MODE.MOBILE ? 
                        (<LearningLibraryMobile 
                            parentState={{
                                groups,
                                periods,
                                filteredArticles,
                                recentArticles,
                                allGroupsSelected,
                                allPeriodsSelected
                            }}
                            parentFunc={{
                                toggleGroup: this.toggleGroup,
                                togglePeriod: this.togglePeriod,
                                toggleAllGroups: this.toggleAllGroups,
                                toggleAllPeriods: this.toggleAllPeriods
                            }} />) : 
                        (<LearningLibraryDesktop
                            parentState={{
                                groups,
                                periods,
                                filteredArticles,
                                recentArticles,
                                allGroupsSelected,
                                allPeriodsSelected
                            }}
                            parentFunc={{
                                toggleGroup: this.toggleGroup,
                                togglePeriod: this.togglePeriod,
                                toggleAllGroups: this.toggleAllGroups,
                                toggleAllPeriods: this.toggleAllPeriods
                            }} />)
                ) : (<Loader />)}
            </Container>
        )
    }

    componentDidMount() {
        logEvent({ 'CDL_Knowledge_Portal_Page_Viewed' : true })
        if (!this.props.cache) {
            graphqlFetch('wcms3', `
                query {
                    learningLibraryGroups {
                        id
                        name
                        order
                        active
                        articles: contentArticleLearningLibraryGroups {
                            article {
                                id
                                title
                                subtitle
                                image
                                featured_article: featuredArticle
                                publication_date: publicationDate
                                synopsis
                                ages: contentArticleAges {
                                    age {
                                        id
                                        name
                                        order
                                    }
                                }
                                article_type: contentArticleArticleTypes {
                                    articletype {
                                        id
                                        name
                                    }
                                }
                                authors: contentArticleAuthors {
                                    author {
                                        id
                                        name
                                    }
                                }
                                ep_type: epType {
                                    id
                                    name
                                }
                                learning_library_group: contentArticleLearningLibraryGroups  {
                                    learninglibrarygroup {
                                        id
                                        name
                                        order
                                        active
                                    }
                                }
                            }
                        }
                    }
                }
                `)
                .then(res => {
                    const groups = res.data.learningLibraryGroups.map(group => ({
                        id: group.id,
                        name: group.name,
                        order: group.order,
                        active: true,
                        articles: group.articles
                            .map(x => x.article)
                            .map(article => ({
                                ...article,
                                ages: article.ages.map(x => x.age),
                                ep_type: article.ep_type?.name,
                                article_type: article.article_type?.map(x => ({ ...x.articletype })),
                                authors: article.authors?.map(x => x.author),
                                learning_library_group: article.learning_library_group?.map(x => x.learninglibrarygroup)
                            }))
                    }))
                    const articles = 
                        groups.map(group => group.articles)
                              .reduce((acc, x) => [...acc, ...x], [])
                              .reduce((acc, x) => {
                                    const exists = acc.find(a => a.id === x.id)
                                    return exists ? acc : [...acc, x]
                                }, [])

                    this.parseServerData({ groups, articles })
                })
        }
        else 
            this.init(this.props.cache)
    }

    parseServerData = (data) => {
        // articles
        const groupedArticles = 
            data.groups
                .sort(sortPropFn('order'))
                .map(group => this.parseServerDataGroup(group, data.articles))
                .filter(group => group.articles.length > 0)
        // const newArticlesGroup = {
        //     groupName: 'Latest Articles',
        //     articles: data.articles.sort(sortPropDescFn('publication_date')).slice(0,6)
        // }

        // groups
        const filteredGroups = 
            data.groups
                .filter(group => groupedArticles.map(x => x.groupName).indexOf(group.name) > -1)
                .map(group => ({ ...group, count: groupedArticles.find(x => x.groupName === group.name).articles.length }))
                .sort(sortPropFn('order'))
        // const newGroup = {
        //     id: 12345,
        //     name: 'Latest Articles',
        //     active: true,
        //     count: 6
        // }

        // periods
        const periods = distinctByProp(
            data.articles
                .map(x => x.ages)
                .reduce((acc, x) => [...acc, ...x], []),
            'id')

        // cache
        const cache = {
            groups: filteredGroups, // [newGroup, ...filteredGroups],
            articles: groupedArticles, //[newArticlesGroup, ...groupedArticles],
            periods: periods.map(x => ({...x, active: true})).sort(sortPropFn('order')),
            recentArticles: data.articles.sort(sortPropDescFn('publication_date')).slice(0,6)
        }

        this.props.setLearningLibraryCache(cache)
        this.init(cache)
    }

    parseServerDataGroup = (group, articles) => {     
        const groupName = group.name
        const groupArticles = 
            articles
                .filter(article =>
                    article.learning_library_group
                        .reduce((acc, llg) => llg.name === groupName || acc, false))
                .sort(sortPropDescFn('publication_date'))

        return {
            groupName,
            articles: groupArticles
        }
    }
}

const mapStateToProps = ({ data }) => ({ 
    cache: data.configuration.learningLibraryCache,
    contentMode: data.configuration.contentMode
})
const mapDispatchToProps = (dispatch) => ({ setLearningLibraryCache: learningLibraryCache => dispatch(setConfiguration({ learningLibraryCache })) })


export default connect(mapStateToProps, mapDispatchToProps)(LearningLibrary)